/** @format */

import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { ThemeProvider, createTheme, CssBaseline } from "@mui/material";
import Header from "./components/Header";
import Home from "./components/Home";
import AboutUs from "./components/AboutUs";
import ContactPage from "./components/Contact";
import Footer from "./components/Footer";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfService from "./components/TermsOfService";
import Blog from "./components/Blog";
import BlogPost from "./components/BlogPost";
import News from "./components/News";
import NewsItem from "./components/NewsItem";
import Download from "./components/Download";
import {
  trackPageView,
  initGA,
  trackEvent,
  trackError,
} from "./utils/analytics";
import Unsubscribe from "./components/Unsubscribe";

const theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#040506",
    },
    secondary: {
      main: "#ffffff",
    },
    background: {
      default: "#040506",
      paper: "#07080a",
    },
  },
  typography: {
    fontFamily:
      "'Inter', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
});

// A component to track route changes
const RouteListener: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    // Get page template type based on the pathname
    const getPageTemplate = (pathname: string): string => {
      if (pathname === "/") return "homepage";
      if (pathname === "/about") return "about";
      if (pathname === "/join") return "pilot-test";
      if (pathname === "/contact") return "contact";
      if (pathname === "/download") return "download-redirect";
      if (pathname.includes("privacy-policy")) return "legal";
      if (pathname.includes("terms-of-service")) return "legal";
      if (pathname.startsWith("/blog")) return "blog";
      if (pathname.startsWith("/news")) return "news";
      return "standard";
    };

    // Track the page view with enhanced information
    const pageTitle = getPageTitle(location.pathname);
    const pageTemplate = getPageTemplate(location.pathname);

    trackPageView(location.pathname, pageTitle, pageTemplate);

    // Track the navigation as an event too
    trackEvent("navigation", {
      from_path: document.referrer,
      to_path: location.pathname,
    });
  }, [location]);

  // Helper to get a descriptive page title
  const getPageTitle = (pathname: string): string => {
    switch (pathname) {
      case "/":
        return "Home | eon.health";
      case "/about":
        return "About Us | eon.health";
      case "/contact":
        return "Contact Us | eon.health";
      case "/download":
        return "Download App | eon.health";
      case "/privacy-policy":
        return "Privacy Policy | eon.health";
      case "/terms-of-service":
        return "Terms of Service | eon.health";
      case "/blog":
        return "Blog | eon.health";
      case "/news":
        return "News | eon.health";
      default:
        if (pathname.startsWith("/blog")) {
          return "Blog Post | eon.health";
        }
        if (pathname.startsWith("/news")) {
          return "News | eon.health";
        }
        return document.title;
    }
  };

  return null;
};

const AppContent: React.FC = () => {
  const location = useLocation();

  // Determine header background color based on route
  const isBlogPostPage = /^\/blog\/.+$/.test(location.pathname);
  const isNewsItemPage = /^\/news\/.+$/.test(location.pathname);
  const headerBackgroundColor = isBlogPostPage || isNewsItemPage
    ? "#fff8f2" // Light background for article pages
    : theme.palette.primary.main; // Dark background for all other pages

  return (
    <>
      <RouteListener />
      <Header backgroundColor={headerBackgroundColor} />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/download" element={<Download />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/news" element={<News />} />
        <Route path="/news/:slug" element={<NewsItem />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
      </Routes>
      <Footer />
    </>
  );
};

const App: React.FC = () => {
  // Initialize Google Analytics when the app loads
  useEffect(() => {
    // Initialize Google Analytics with enhanced configuration
    initGA();

    // Track errors globally
    window.addEventListener("error", (event) => {
      const { message, filename, lineno, colno, error } = event;
      trackError(
        "javascript_error",
        message || "Unknown error",
        `${filename}:${lineno}:${colno}`
      );
    });

    // Track browser performance metrics
    if ("performance" in window && "getEntriesByType" in window.performance) {
      window.addEventListener("load", () => {
        setTimeout(() => {
          const perfEntries = window.performance.getEntriesByType("navigation");
          if (perfEntries.length > 0) {
            const navEntry = perfEntries[0] as PerformanceNavigationTiming;
            trackEvent("performance_metrics", {
              dns_time: Math.round(
                navEntry.domainLookupEnd - navEntry.domainLookupStart
              ),
              connection_time: Math.round(
                navEntry.connectEnd - navEntry.connectStart
              ),
              ttfb: Math.round(navEntry.responseStart - navEntry.requestStart),
              dom_load_time: Math.round(
                navEntry.domContentLoadedEventEnd - navEntry.startTime
              ),
              page_load_time: Math.round(
                navEntry.loadEventEnd - navEntry.startTime
              ),
            });
          }
        }, 0);
      });
    }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  );
};

export default App;
